(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-song-launch-root]';
  const META_SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';

  const safeStorage = {
    get(storage, key) {
      try {
        return storage.getItem(key);
      } catch (_) {
        return null;
      }
    },
    set(storage, key, value) {
      try {
        storage.setItem(key, value);
      } catch (_) {
        // El almacenamiento puede estar bloqueado en navegación privada.
      }
    }
  };

  function createId(prefix = 'sl') {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return `${prefix}_${window.crypto.randomUUID()}`;
    }
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(Number(value) || 0, min), max);
  }

  function formatTime(value) {
    const seconds = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
  }

  function getCookie(name) {
    const prefix = `${encodeURIComponent(name)}=`;
    const match = document.cookie
      .split(';')
      .map((item) => item.trim())
      .find((item) => item.startsWith(prefix));
    return match ? decodeURIComponent(match.slice(prefix.length)) : '';
  }

  function getAttribution() {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get('fbclid') || '';
    const fbcCookie = getCookie('_fbc');
    const generatedFbc = fbclid ? `fb.1.${Date.now()}.${fbclid}` : '';

    return {
      fbclid,
      fbp: getCookie('_fbp'),
      fbc: fbcCookie || generatedFbc,
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
      utm_term: params.get('utm_term') || ''
    };
  }

  function getVisitorId() {
    const key = 'song_launch_visitor_id';
    let value = safeStorage.get(window.localStorage, key);
    if (!value) {
      value = createId('visitor');
      safeStorage.set(window.localStorage, key, value);
    }
    return value;
  }

  function parseConfig(root) {
    const node = root.querySelector('[data-song-launch-config]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[Song Launch] Configuración inválida.', error);
      return null;
    }
  }

  function loadMetaPixel(pixelId) {
    if (!pixelId) return false;

    if (!window.fbq) {
      const fbq = function () {
        fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
      };
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = '2.0';
      fbq.queue = [];
      window.fbq = fbq;
      window._fbq = fbq;

      const script = document.createElement('script');
      script.async = true;
      script.src = META_SCRIPT_URL;
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        (document.head || document.documentElement).appendChild(script);
      }
    }

    window.__songLaunchPixelIds = window.__songLaunchPixelIds || new Set();
    if (!window.__songLaunchPixelIds.has(pixelId)) {
      window.fbq('init', pixelId);
      window.__songLaunchPixelIds.add(pixelId);
    }

    return true;
  }

  class MetaTracker {
    constructor(config) {
      this.config = config;
      this.active = false;
      this.started = false;
      this.readyCallbacks = [];
      this.pageViewSent = false;
    }

    log(...args) {
      if (this.config.debug) {
        console.log('[Song Launch]', ...args);
      }
    }

    onReady(callback) {
      if (this.active) {
        callback();
        return;
      }
      this.readyCallbacks.push(callback);
    }

    start() {
      if (this.started || !this.config.trackingEnabled || this.config.designMode) return;
      if (!this.config.pixelId && !this.config.capiEndpoint) return;

      this.started = true;
      this.active = true;
      loadMetaPixel(this.config.pixelId);
      this.sendPageView();

      this.readyCallbacks.splice(0).forEach((callback) => {
        try {
          callback();
        } catch (error) {
          this.log('Error al iniciar callback de seguimiento.', error);
        }
      });
    }

    stop() {
      this.active = false;
      this.log('Seguimiento detenido por consentimiento.');
    }

    sendPageView() {
      if (this.pageViewSent) return;
      this.pageViewSent = this.track('PageView', {
        content_name: this.config.songName || document.title,
        content_category: 'Music'
      });
    }

    track(eventName, customData = {}, options = {}) {
      if (!this.active || !eventName) return false;

      const eventId = createId(eventName.toLowerCase());
      const normalizedData = {
        content_name: this.config.songName || '',
        content_category: 'Music',
        artist_name: this.config.artistName || '',
        destination: 'Spotify',
        ...customData
      };

      if (this.config.pixelId && window.fbq) {
        try {
          const pixelCommand = options.custom ? 'trackCustom' : 'track';
          window.fbq(pixelCommand, eventName, normalizedData, { eventID: eventId });
        } catch (error) {
          this.log('Error enviando evento por Meta Pixel.', error);
        }
      }

      if (this.config.capiEndpoint) {
        try {
          this.sendCapi(eventName, eventId, normalizedData);
        } catch (error) {
          this.log('Error iniciando envío por CAPI.', error);
        }
      }

      this.log(eventName, {
        eventId,
        eventType: options.custom ? 'custom' : 'standard',
        ...normalizedData
      });
      return true;
    }

    trackCustom(eventName, customData = {}) {
      return this.track(eventName, customData, { custom: true });
    }

    sendCapi(eventName, eventId, customData) {
      const attribution = getAttribution();
      const payload = {
        source: 'shopify_music_song_launch',
        event_name: eventName,
        event_id: eventId,
        event_time: Math.floor(Date.now() / 1000),
        action_source: 'website',
        event_source_url: window.location.href,
        page_title: document.title,
        referrer: document.referrer || '',
        user_agent: navigator.userAgent,
        external_id: getVisitorId(),
        test_event_code: this.config.testEventCode || '',
        song_name: this.config.songName || '',
        artist_name: this.config.artistName || '',
        spotify_url: this.config.spotifyUrl || '',
        custom_data: customData,
        ...attribution
      };

      const body = JSON.stringify(payload);

      try {
        if (navigator.sendBeacon) {
          const sent = navigator.sendBeacon(
            this.config.capiEndpoint,
            new Blob([body], { type: 'text/plain;charset=UTF-8' })
          );
          if (sent) return;
        }
      } catch (error) {
        this.log('sendBeacon no disponible.', error);
      }

      fetch(this.config.capiEndpoint, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body
      }).catch((error) => this.log('Error enviando CAPI.', error));
    }
  }

  function initializeConsent(tracker) {
    if (!tracker.config.trackingEnabled || tracker.config.designMode) return;

    if (!tracker.config.requireMarketingConsent) {
      tracker.start();
      return;
    }

    const applyConsent = (allowed) => {
      if (allowed) tracker.start();
      else tracker.stop();
    };

    document.addEventListener('visitorConsentCollected', (event) => {
      const detail = event && event.detail ? event.detail : {};
      applyConsent(Boolean(detail.marketingAllowed));
    });

    const checkPrivacyApi = () => {
      const api = window.Shopify && window.Shopify.customerPrivacy;
      if (!api || typeof api.marketingAllowed !== 'function') {
        tracker.log('Customer Privacy API no disponible; seguimiento bloqueado.');
        return;
      }
      applyConsent(Boolean(api.marketingAllowed()));
    };

    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (error) {
            tracker.log('No se pudo cargar Customer Privacy API.', error);
            return;
          }
          checkPrivacyApi();
        }
      );
    } else {
      checkPrivacyApi();
    }
  }


  function initializePlayer(root, config, tracker) {
    const player = root.querySelector('[data-song-player]');
    const audio = root.querySelector('[data-song-audio]');
    const toggle = root.querySelector('[data-song-toggle]');
    const range = root.querySelector('[data-song-range]');
    const current = root.querySelector('[data-song-current]');
    const duration = root.querySelector('[data-song-duration]');
    const platformButtons = Array.from(root.querySelectorAll('[data-platform-button]'));
    const soundGate = root.querySelector('[data-sound-gate]');
    const previewEndedMessage = root.querySelector('[data-preview-ended]');

    if (!player || !audio || !toggle || !range || !current || !duration) return;

    const songKey = `${config.artistName || 'artist'}_${config.songName || 'song'}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .slice(0, 80);
    const viewStorageKey = `song_launch_events_v2_view_${songKey}`;
    const listen10StorageKey = `song_launch_events_v2_listen10_${songKey}`;
    const previewCompletedStorageKey = `song_launch_events_v2_preview_completed_${songKey}`;
    const getLeadStorageKey = (platform) => {
      const platformKey = String(platform || 'music')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      return `song_launch_events_v3_lead_${songKey}_${platformKey}`;
    };

    const baseVolume = clamp(config.audioVolume, 0.2, 1);
    const fadeStart = Math.max(0, Number(config.fadeStartSeconds) || 16);
    const fadeDuration = Math.max(0.5, Number(config.fadeDurationSeconds) || 4);
    const previewEnd = fadeStart + fadeDuration;

    let listenedSeconds = 0;
    let lastTick = null;
    let viewSent = safeStorage.get(window.sessionStorage, viewStorageKey) === '1';
    let listen10Sent = safeStorage.get(window.sessionStorage, listen10StorageKey) === '1';
    let previewCompletedSent =
      safeStorage.get(window.sessionStorage, previewCompletedStorageKey) === '1';
    let previewFinished = false;
    let autoplayAttempted = false;
    let animationFrame = 0;
    let lastUiUpdate = 0;

    let audioContext = null;
    let mediaSource = null;
    let gainNode = null;
    let audioGraphFailed = false;

    audio.volume = baseVolume;
    audio.muted = false;

    const getVisibleDuration = () => {
      const realDuration = Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : previewEnd;
      return Math.min(realDuration, previewEnd);
    };

    const setPreviewFinished = (finished) => {
      previewFinished = finished;
      player.classList.toggle('is-preview-ended', finished);
      if (previewEndedMessage) previewEndedMessage.hidden = !finished;
    };

    const setNativeVolume = (value) => {
      const normalized = clamp(value, 0, 1);
      try {
        audio.volume = normalized;
      } catch (_) {
        // Algunos navegadores móviles ignoran audio.volume.
      }
    };

    const setGainImmediately = (value) => {
      const normalized = clamp(value, 0, 1);

      if (gainNode && audioContext) {
        const now = audioContext.currentTime;
        gainNode.gain.cancelScheduledValues(now);
        gainNode.gain.setValueAtTime(normalized, now);
        return;
      }

      setNativeVolume(normalized);
    };

    const ensureAudioGraph = async () => {
      if (audioGraphFailed) return false;

      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return false;

      try {
        if (!audioContext) {
          audioContext = new AudioContextClass();
          mediaSource = audioContext.createMediaElementSource(audio);
          gainNode = audioContext.createGain();
          gainNode.gain.value = baseVolume;
          mediaSource.connect(gainNode);
          gainNode.connect(audioContext.destination);

          // El volumen se controla desde GainNode cuando Web Audio está disponible.
          setNativeVolume(1);
        }

        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        return audioContext.state === 'running';
      } catch (error) {
        audioGraphFailed = true;
        tracker.log('No se pudo inicializar Web Audio; se usa audio.volume.', error);
        return false;
      }
    };

    const scheduleFade = () => {
      const time = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;

      if (!gainNode || !audioContext || audioContext.state !== 'running') {
        return;
      }

      const now = audioContext.currentTime;
      const gain = gainNode.gain;
      gain.cancelScheduledValues(now);

      if (time >= previewEnd) {
        gain.setValueAtTime(0, now);
        return;
      }

      if (time < fadeStart) {
        const fadeDelay = Math.max(0, fadeStart - time);
        const fadeEndDelay = Math.max(fadeDelay + 0.01, previewEnd - time);
        gain.setValueAtTime(baseVolume, now);
        gain.setValueAtTime(baseVolume, now + fadeDelay);
        gain.linearRampToValueAtTime(0, now + fadeEndDelay);
        return;
      }

      const remaining = Math.max(0.01, previewEnd - time);
      const currentGain = baseVolume * clamp(remaining / fadeDuration, 0, 1);
      gain.setValueAtTime(currentGain, now);
      gain.linearRampToValueAtTime(0, now + remaining);
    };

    const applyFallbackFade = () => {
      if (gainNode && audioContext && audioContext.state === 'running') return;

      const time = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;

      if (time < fadeStart) {
        setNativeVolume(baseVolume);
        return;
      }

      if (time < previewEnd) {
        const progress = clamp((time - fadeStart) / fadeDuration, 0, 1);
        setNativeVolume(baseVolume * (1 - progress));
        return;
      }

      setNativeVolume(0);
    };

    const resetPreview = () => {
      setPreviewFinished(false);
      audio.currentTime = 0;
      setGainImmediately(baseVolume);
      updatePlayer(true);
    };

    const updatePlayer = (force = false) => {
      const now = performance.now();
      if (!force && now - lastUiUpdate < 80) return;
      lastUiUpdate = now;

      const visibleDuration = getVisibleDuration();
      const audioCurrent = Number.isFinite(audio.currentTime)
        ? Math.min(audio.currentTime, visibleDuration)
        : 0;

      range.max = visibleDuration || previewEnd;
      range.value = audioCurrent;
      current.textContent = formatTime(audioCurrent);
      duration.textContent = formatTime(visibleDuration);
    };

    const accumulateListening = () => {
      const now = performance.now();
      if (lastTick !== null && !audio.paused && !audio.seeking && !audio.ended) {
        const delta = (now - lastTick) / 1000;
        if (delta > 0 && delta < 2.5) {
          listenedSeconds += delta;
        }
      }
      lastTick = audio.paused || audio.seeking || audio.ended ? null : now;
    };

    const sendViewContentFromPlayClick = () => {
      if (viewSent) return;

      const sent = tracker.track('ViewContent', {
        interaction: 'manual_play_click',
        preview_seconds: Math.floor(Math.min(listenedSeconds, previewEnd))
      });

      if (sent) {
        viewSent = true;
        safeStorage.set(window.sessionStorage, viewStorageKey, '1');
      }
    };

    const maybeSendListen10Seconds = () => {
      if (listen10Sent || listenedSeconds < 10) return;

      const sent = tracker.trackCustom('Listen10Seconds', {
        preview_seconds: 10,
        actual_listened_seconds: Math.floor(Math.min(listenedSeconds, previewEnd)),
        qualified_preview: true
      });

      if (sent) {
        listen10Sent = true;
        safeStorage.set(window.sessionStorage, listen10StorageKey, '1');
      }
    };

    const maybeSendPreviewCompleted = () => {
      if (previewCompletedSent) return;

      // Exige tiempo real escuchado; mover la barra hasta el final no cuenta.
      const completionThreshold = Math.max(10, previewEnd - 1.5);
      if (listenedSeconds < completionThreshold) return;

      const sent = tracker.trackCustom('PreviewCompleted', {
        preview_seconds: Math.round(previewEnd),
        actual_listened_seconds: Math.floor(Math.min(listenedSeconds, previewEnd)),
        qualified_preview: true
      });

      if (sent) {
        previewCompletedSent = true;
        safeStorage.set(window.sessionStorage, previewCompletedStorageKey, '1');
      }
    };

    const finishPreview = () => {
      if (previewFinished) return;

      setGainImmediately(0);
      if (!audio.paused) audio.pause();

      if (Number.isFinite(audio.duration)) {
        audio.currentTime = Math.min(previewEnd, audio.duration);
      }

      setPreviewFinished(true);
      updatePlayer(true);
      maybeSendListen10Seconds();
      maybeSendPreviewCompleted();
    };

    const playbackFrame = () => {
      animationFrame = 0;
      accumulateListening();

      const time = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
      if (time >= previewEnd - 0.015) {
        finishPreview();
        return;
      }

      applyFallbackFade();
      updatePlayer();
      maybeSendListen10Seconds();

      if (!audio.paused && !audio.ended) {
        animationFrame = window.requestAnimationFrame(playbackFrame);
      }
    };

    const startPlaybackFrame = () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      lastTick = performance.now();
      animationFrame = window.requestAnimationFrame(playbackFrame);
    };

    const stopPlaybackFrame = () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = 0;
      }
      accumulateListening();
    };

    const hideSoundGate = () => {
      if (soundGate) soundGate.hidden = true;
    };

    const showSoundGate = () => {
      if (soundGate && audio.src && !config.designMode) soundGate.hidden = false;
    };

    const startPlayback = async ({ restart = false, userInitiated = false } = {}) => {
      if (!audio.src) return false;

      if (restart || previewFinished || audio.currentTime >= previewEnd - 0.05) {
        resetPreview();
      }

      audio.muted = false;

      if (userInitiated) {
        await ensureAudioGraph();
      }

      if (audio.currentTime < fadeStart) {
        setGainImmediately(baseVolume);
      }

      try {
        await audio.play();

        if (gainNode && audioContext && audioContext.state === 'running') {
          scheduleFade();
        }

        hideSoundGate();
        return true;
      } catch (error) {
        showSoundGate();
        tracker.log('El navegador bloqueó la reproducción automática con sonido.', error);
        return false;
      }
    };

    const attemptAutoplay = () => {
      if (autoplayAttempted || !config.autoplayAudio || config.designMode || !audio.src) return;
      autoplayAttempted = true;
      startPlayback({ userInitiated: false });
    };

    tracker.onReady(() => {
      maybeSendListen10Seconds();
      if (previewFinished) maybeSendPreviewCompleted();
    });

    toggle.addEventListener('click', async () => {
      if (!audio.src) return;

      if (audio.paused) {
        const started = await startPlayback({
          restart: previewFinished,
          userInitiated: true
        });

        // ViewContent corresponde únicamente a una pulsación manual en Play.
        // Se envía después de ejecutar Play para no afectar el reproductor.
        if (started) {
          window.setTimeout(sendViewContentFromPlayClick, 0);
        }
      } else {
        audio.pause();
      }
    });

    if (soundGate) {
      soundGate.addEventListener('click', async () => {
        await startPlayback({ restart: previewFinished, userInitiated: true });
      });
    }

    const unlockOnFirstInteraction = (event) => {
      const target = event.target instanceof Element ? event.target : null;

      if (
        target &&
        target.closest('[data-song-toggle], [data-song-range], [data-platform-button], [data-sound-gate]')
      ) {
        return;
      }

      if (config.autoplayAudio && audio.paused && !previewFinished) {
        startPlayback({ userInitiated: true });
      }
    };

    document.addEventListener('pointerdown', unlockOnFirstInteraction, { once: true, capture: true });
    document.addEventListener('keydown', unlockOnFirstInteraction, { once: true, capture: true });

    audio.addEventListener('loadedmetadata', () => {
      updatePlayer(true);
      attemptAutoplay();
    });

    audio.addEventListener('canplay', attemptAutoplay, { once: true });
    audio.addEventListener('durationchange', () => updatePlayer(true));

    audio.addEventListener('play', () => {
      player.classList.add('is-playing');
      toggle.setAttribute('aria-label', 'Duraklat');
      hideSoundGate();

      if (gainNode && audioContext && audioContext.state === 'running') {
        scheduleFade();
      }

      startPlaybackFrame();
    });

    audio.addEventListener('playing', () => {
      if (gainNode && audioContext && audioContext.state === 'running') {
        scheduleFade();
      }
      startPlaybackFrame();
    });

    audio.addEventListener('pause', () => {
      stopPlaybackFrame();
      player.classList.remove('is-playing');
      toggle.setAttribute('aria-label', 'Oynat');
      maybeSendListen10Seconds();

      if (!previewFinished) {
        const time = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
        if (gainNode && audioContext && audioContext.state === 'running') {
          const remaining = Math.max(0, previewEnd - time);
          const currentGain = time < fadeStart
            ? baseVolume
            : baseVolume * clamp(remaining / fadeDuration, 0, 1);
          setGainImmediately(currentGain);
        }
      }
    });

    audio.addEventListener('ended', () => {
      stopPlaybackFrame();
      player.classList.remove('is-playing');
      toggle.setAttribute('aria-label', 'Oynat');
      finishPreview();
    });

    audio.addEventListener('waiting', () => {
      stopPlaybackFrame();
      lastTick = null;
    });

    audio.addEventListener('seeking', () => {
      stopPlaybackFrame();
      lastTick = null;
    });

    audio.addEventListener('seeked', () => {
      if (audio.currentTime >= previewEnd) {
        finishPreview();
        return;
      }

      setPreviewFinished(false);

      if (gainNode && audioContext && audioContext.state === 'running') {
        scheduleFade();
      } else {
        applyFallbackFade();
      }

      if (!audio.paused) startPlaybackFrame();
      updatePlayer(true);
    });

    audio.addEventListener('timeupdate', () => {
      if (audio.currentTime >= previewEnd - 0.015) {
        finishPreview();
        return;
      }

      applyFallbackFade();
      updatePlayer();
      maybeSendListen10Seconds();
    });

    range.addEventListener('input', () => {
      const selectedTime = Math.min(
        Number(range.value) || 0,
        getVisibleDuration(),
        previewEnd
      );

      audio.currentTime = selectedTime;

      if (selectedTime >= previewEnd) {
        finishPreview();
        return;
      }

      setPreviewFinished(false);

      if (gainNode && audioContext && audioContext.state === 'running') {
        scheduleFade();
      } else {
        applyFallbackFade();
      }

      updatePlayer(true);
    });

    platformButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        if (config.designMode) {
          event.preventDefault();
          return;
        }

        stopPlaybackFrame();
        if (!audio.paused) audio.pause();

        accumulateListening();
        maybeSendListen10Seconds();

        const platform = button.dataset.platform || 'Music';
        const destinationUrl =
          button.dataset.platformUrl ||
          button.getAttribute('href') ||
          '';
        const leadStorageKey = getLeadStorageKey(platform);
        const leadAlreadySent =
          safeStorage.get(window.sessionStorage, leadStorageKey) === '1';

        let leadTracked = false;

        if (!leadAlreadySent) {
          leadTracked = tracker.track('Lead', {
            destination: platform,
            preview_seconds: Math.floor(Math.min(listenedSeconds, previewEnd)),
            listened_10_seconds: listen10Sent || listenedSeconds >= 10,
            preview_completed: previewCompletedSent,
            outbound_destination: platform,
            outbound_url: destinationUrl,
            platform_choice: platform
          });

          if (leadTracked) {
            safeStorage.set(window.sessionStorage, leadStorageKey, '1');
          }
        }

        const opensNewTab = button.getAttribute('target') === '_blank';

        // Si abre en la misma pestaña, damos un margen mínimo para que Pixel
        // y sendBeacon despachen Lead antes de salir de la landing.
        if (!opensNewTab && leadTracked && destinationUrl) {
          event.preventDefault();
          window.setTimeout(() => {
            window.location.assign(destinationUrl);
          }, 160);
        }
      });
    });

    updatePlayer(true);

    if (config.autoplayAudio && !config.designMode && audio.src) {
      window.setTimeout(attemptAutoplay, 80);
    }
  }

  function initializeRoot(root) {
    if (!root || root.dataset.songLaunchInitialized === 'true') return;

    const config = parseConfig(root);
    if (!config) return;

    root.dataset.songLaunchInitialized = 'true';
    const tracker = new MetaTracker(config);
    initializePlayer(root, config, tracker);
    initializeConsent(tracker);
  }

  function initializeAll(scope = document) {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) {
      initializeRoot(scope);
    }
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initializeRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });
})();
